import { useNavigate } from 'react-router-dom';
import MdLicenseBanner from "../components/MdLicenseBanner";
import { useState, useEffect, useCallback, useRef } from 'react';
import {
  Monitor, RefreshCw, Search, Wifi, Server, Camera, Router,
  Network, HardDrive, Activity, Clock, CheckCircle,
  Play, Loader2, AlertCircle, Shield, X, Settings, Eye,
  DoorOpen, Link2, ChevronDown, Trash2, RotateCcw, Edit3
, XCircle, HelpCircle } from 'lucide-react';
import { toast } from 'react-toastify';
import fetchWithTenant from '../utils/fetchWithTenant';
import StatusTooltip from '../components/StatusTooltip';
import useAuthStore from '../stores/authStore';
import useTutorialStore from '../stores/tutorialStore';
import useTutorialPrompt from '../hooks/useTutorialPrompt';
import TutorialPrompt from '../components/tutorials/TutorialPrompt';
import discoveryTutorial from '../components/tutorials/configs/discoveryTutorial';

import DevicesTabBar from '../components/DevicesTabBar';
// Device type icon mapping
const deviceIcons = {
  CAMERA: Camera,
  SERVER: Server,
  ROUTER: Router,
  SWITCH: Network,
  NVR: HardDrive,
  UNKNOWN: Monitor,
  ACCESS_POINT: Wifi,
  ACCESS_CONTROLLER: DoorOpen,
  INTERCOM: Activity,
  PRINTER: Activity,
};

// Format relative time
const formatRelativeTime = (date) => {
  if (!date) return 'Never';
  const now = new Date();
  const then = new Date(date);
  const diffMs = now - then;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  return `${diffDays}d ago`;
};

// Natural IP sort: 192.168.1.2 before 192.168.1.10
function compareIPs(a, b) {
  const pa = (a || "").split(".").map(Number);
  const pb = (b || "").split(".").map(Number);
  for (let i = 0; i < 4; i++) {
    if ((pa[i] || 0) !== (pb[i] || 0)) return (pa[i] || 0) - (pb[i] || 0);
  }
  return 0;
}

const MdDiscovery = () => {
  const startTutorial = useTutorialStore((s) => s.startTutorial);
  const { showPrompt, dismissTutorial } = useTutorialPrompt(discoveryTutorial);

  // State
  const { tenantVersion } = useAuthStore();
  const [devices, setDevices] = useState([]);
  const navigate = useNavigate();
  const [agents, setAgents] = useState([]);
  // Which agent runs the scan / owns the scan-config being viewed. HOST_COLLECTOR
  // agents are excluded entirely -- they must never appear as a scan target.
  const [selectedAgentId, setSelectedAgentId] = useState('');
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [scanProgress, setScanProgress] = useState(null);
  const scanPollRef = useRef(null);
  const [activeFilter, setActiveFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [stats, setStats] = useState({
    total: 0,
    cameras: 0,
    accessControl: 0,
    servers: 0,
    network: 0,
    online: 0,
  });

  // Integration sync state
  const [syncing, setSyncing] = useState(false);
  const [syncStatus, setSyncStatus] = useState([]);
  const [showSyncDropdown, setShowSyncDropdown] = useState(false);

  // Selection state for promotion
  const [selectedIds, setSelectedIds] = useState(new Set());
  // Mobile card expand state (mirrors MdMonitoredDevices: a Set so multiple
  // cards can be open at once)
  const [expandedCards, setExpandedCards] = useState(new Set());
  const toggleCard = (id) => {
    setExpandedCards(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  const [rescanning, setRescanning] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [editTypeDevice, setEditTypeDevice] = useState(null);
  const [editTypeValue, setEditTypeValue] = useState('');
  const [editTypeId, setEditTypeId] = useState(null);
  const [showPromoteModal, setShowPromoteModal] = useState(false);
  const [promoting, setPromoting] = useState(false);
  const [promoteConfig, setPromoteConfig] = useState({
    monitoringProfile: 'PING',
    pollIntervalSeconds: 60,
    alertThresholdSeconds: 120,
    group: '',
  });
  // Scan config state
  const [showScanConfig, setShowScanConfig] = useState(false);
  const [scanConfig, setScanConfig] = useState({ defaultNetworks: [], schedule: { enabled: false, time: "00:00" } });
  const [networkInput, setNetworkInput] = useState("");
  const [savingScanConfig, setSavingScanConfig] = useState(false);

  // Fetch devices

  // Reset on tenant switch
  useEffect(() => {
    setDevices([]); setLoading(true);
  }, [tenantVersion]);

  const fetchDevices = useCallback(async (showToast = false) => {
    console.log("[MD Discovery] Fetching devices...");
    try {
      const response = await fetchWithTenant('/api/md/devices');
      if (response.ok) {
        const data = await response.json();
        const deviceList = data.devices || data || [];
        setDevices(deviceList);
        console.log("[MD Discovery] Received", deviceList.length, "devices");

        // Calculate stats
        setStats({
          total: deviceList.length,
          cameras: deviceList.filter(d => d.deviceType === 'CAMERA').length,
          accessControl: deviceList.filter(d => d.deviceType === 'ACCESS_CONTROLLER').length,
          servers: deviceList.filter(d => d.deviceType === 'SERVER').length,
          network: deviceList.filter(d => ['ROUTER', 'SWITCH', 'ACCESS_POINT'].includes(d.deviceType)).length,
          online: deviceList.filter(d => d.status === 'ONLINE').length,
          wireless: deviceList.filter(d => d.isWireless).length,
        });
      }
    } catch (error) {
      console.error('Failed to fetch devices:', error);
      toast.error('Failed to load devices');
    } finally {
      setLoading(false);
    }
  }, [tenantVersion]);

  // Fetch agents
  const fetchAgents = useCallback(async () => {
    try {
      const response = await fetchWithTenant('/api/md/agents');
      if (response.ok) {
        const data = await response.json();
        setAgents(data || []);
      }
    } catch (error) {
      console.error('Failed to fetch agents:', error);
    }
  }, [tenantVersion]);

  // Agents eligible to run a scan -- HOST_COLLECTOR agents must never appear
  // as a scan target, not just be blocked server-side.
  const eligibleAgents = agents.filter(a => a.role !== 'HOST_COLLECTOR');
  const selectedAgent = eligibleAgents.find(a => a.id === selectedAgentId) || null;
  const isSelectedAgentOnline = !!selectedAgent && (selectedAgent.isOnline || selectedAgent.status === 'ONLINE');

  // Default-select the first available (online, non-HOST_COLLECTOR) agent once
  // agents load, so existing single-agent tenants see no behavior change. Left
  // alone once a valid selection exists so refetches don't clobber a manual pick.
  useEffect(() => {
    if (selectedAgentId && eligibleAgents.some(a => a.id === selectedAgentId)) return;
    const defaultAgent = eligibleAgents.find(a => a.isOnline || a.status === 'ONLINE') || eligibleAgents[0] || null;
    setSelectedAgentId(defaultAgent ? defaultAgent.id : '');
  }, [agents]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch integration sync status
  const fetchSyncStatus = useCallback(async () => {
    try {
      const response = await fetchWithTenant('/api/md/integrations/sync-status');
      if (response.ok) {
        const data = await response.json();
        setSyncStatus(data.integrations || []);
      }
    } catch (error) {
      console.error('Failed to fetch sync status:', error);
    }
  }, [tenantVersion]);

  // Sync all integrations
  const syncIntegrations = async () => {
    setSyncing(true);
    setShowSyncDropdown(false);
    try {
      const response = await fetchWithTenant('/api/md/integrations/sync', {
        method: 'POST',
      });

      if (response.ok) {
        const data = await response.json();
        const results = data.result || {};
        
        // Build success message
        const parts = [];
        if (results.cameras?.added > 0) parts.push(`${results.cameras.added} cameras`);
        if (results.controllers?.added > 0) parts.push(`${results.controllers.added} controllers`);
        if (results.doors?.added > 0) parts.push(`${results.doors.added} doors`);
        
        if (parts.length > 0) {
          toast.success(`Synced: ${parts.join(', ')}`);
        } else {
          toast.info('Integrations synced - no new devices found');
        }
        
        // Refresh devices and sync status
        fetchDevices();
        fetchSyncStatus();
      } else {
        const error = await response.json();
        toast.error(error.error || 'Failed to sync integrations');
      }
    } catch (error) {
      console.error('Sync error:', error);
      toast.error('Failed to sync integrations');
    } finally {
      setSyncing(false);
    }
  };
  // Fetch scan config for the selected agent
  const fetchScanConfig = useCallback(async () => {
    if (!selectedAgent) return;
    try {
      const res = await fetchWithTenant("/api/md/agents/" + selectedAgent.id + "/scan-config");
      if (res.ok) {
        const data = await res.json();
        const cfg = data.scanConfig || {};
        setScanConfig({
          defaultNetworks: cfg.defaultNetworks || [],
          schedule: cfg.schedule || { enabled: false, time: "00:00" }
        });
      }
    } catch (e) { console.error("Failed to fetch scan config:", e); }
  }, [selectedAgent]);

  useEffect(() => { fetchScanConfig(); }, [fetchScanConfig]);

  // On mount: check if a scan is already running (e.g. user navigated away and back)
  useEffect(() => {
    let cancelled = false;
    const checkActiveScan = async () => {
      try {
        const res = await fetchWithTenant('/api/md/scan/status');
        if (!res.ok || cancelled) return;
        const state = await res.json();
        if (state && state.status === 'scanning') {
          setScanning(true);
          setScanProgress(state);
          // Resume polling
          if (scanPollRef.current) clearInterval(scanPollRef.current);
          scanPollRef.current = setInterval(async () => {
            try {
              const statusRes = await fetchWithTenant('/api/md/scan/status');
              if (!statusRes.ok) return;
              const s = await statusRes.json();
              setScanProgress(s);
              if (s.status === 'complete') {
                clearInterval(scanPollRef.current);
                scanPollRef.current = null;
                fetchDevices();
                setTimeout(() => { setScanning(false); setScanProgress(null); }, 3000);
                toast.success('Scan complete! Found ' + (s.devicesFound || 0) + ' devices');
              }
            } catch (e) { console.error('Failed to poll scan status:', e); }
          }, 2000);
        }
      } catch (e) { /* ignore - no active scan */ }
    };
    checkActiveScan();
    return () => { cancelled = true; };
  }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  const saveScanConfig = async () => {
    if (!selectedAgent) return;
    setSavingScanConfig(true);
    try {
      const res = await fetchWithTenant("/api/md/agents/" + selectedAgent.id + "/scan-config", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(scanConfig)
      });
      if (res.ok) {
        toast.success("Scan configuration saved");
        setShowScanConfig(false);
      } else {
        const err = await res.json();
        toast.error(err.error || "Failed to save config");
      }
    } catch (e) {
      toast.error("Failed to save config");
    } finally { setSavingScanConfig(false); }
  };

  const addNetwork = () => {
    const val = networkInput.trim();
    if (!val) return;
    if (scanConfig.defaultNetworks.includes(val)) { toast.error("Already added"); return; }
    setScanConfig(c => ({ ...c, defaultNetworks: [...c.defaultNetworks, val] }));
    setNetworkInput("");
  };

  const removeNetwork = (idx) => {
    setScanConfig(c => ({ ...c, defaultNetworks: c.defaultNetworks.filter((_, i) => i !== idx) }));
  };


  // Trigger network scan
  const triggerScan = async () => {
    if (agents.length === 0) {
      toast.error('No agents available. Please pair an agent first.');
      return;
    }
    if (!selectedAgent) {
      toast.error('Select an agent to scan with.');
      return;
    }
    if (!isSelectedAgentOnline) {
      toast.error(`${selectedAgent.name} is not currently online.`);
      return;
    }
    setScanning(true);
    setScanProgress({ phase: 0, totalPhases: 9, phaseName: 'Starting scan...', status: 'scanning' });
    try {
      const response = await fetchWithTenant('/api/md/scan', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ agentId: selectedAgent.id, networks: scanConfig.defaultNetworks }),
      });
      if (response.ok) {
        toast.success('Network scan started');
        if (scanPollRef.current) clearInterval(scanPollRef.current);
        scanPollRef.current = setInterval(async () => {
          try {
            const statusRes = await fetchWithTenant('/api/md/scan/status');
            if (statusRes.ok) {
              const state = await statusRes.json();
              setScanProgress(state);
              if (state.status === 'complete') {
                clearInterval(scanPollRef.current);
                scanPollRef.current = null;
                fetchDevices();
                setTimeout(() => { setScanning(false); setScanProgress(null); }, 3000);
                toast.success('Scan complete! Found ' + (state.devicesFound || 0) + ' devices');
              }
            }
          } catch (e) { console.error('Failed to poll scan status:', e); }
        }, 2000);
        setTimeout(fetchDevices, 15000);
        setTimeout(fetchDevices, 30000);
        setTimeout(() => {
          if (scanPollRef.current) {
            clearInterval(scanPollRef.current);
            scanPollRef.current = null;
            setScanning(false);
            setScanProgress(null);
            fetchDevices();
          }
        }, 900000);
      } else {
        const error = await response.json();
        toast.error(error.error || 'Failed to start scan');
        setScanning(false);
        setScanProgress(null);
      }
    } catch (error) {
      console.error('Scan error:', error);
      toast.error('Failed to start scan');
      setScanning(false);
      setScanProgress(null);
    }
  };


  const cancelScan = async () => {
    try {
      const response = await fetchWithTenant('/api/md/scan/cancel', { method: 'POST' });
      if (response.ok) {
        toast.success('Scan cancelled');
        if (scanPollRef.current) clearInterval(scanPollRef.current);
        setScanning(false);
        setScanProgress(null);
        fetchDevices();
      } else {
        toast.error('Failed to cancel scan');
      }
    } catch (error) {
      console.error('Cancel scan error:', error);
      toast.error('Failed to cancel scan');
    }
  };
  // Cleanup scan poll on unmount
  useEffect(() => {
    return () => { if (scanPollRef.current) clearInterval(scanPollRef.current); };
  }, []);

  // Selection handlers
  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === filteredDevices.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filteredDevices.map(d => d.id)));
    }
  };

  // Promote selected devices
  const handlePromote = async () => {
    if (selectedIds.size === 0) return;
    setPromoting(true);
    try {
      const defaults = { ...promoteConfig };
      if (!defaults.group) delete defaults.group;

      const response = await fetchWithTenant('/api/md/monitored/promote', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          deviceIds: Array.from(selectedIds),
          defaults,
        }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        const msg = [];
        if (data.promoted > 0) msg.push(`${data.promoted} device${data.promoted > 1 ? 's' : ''} added to monitoring`);
        if (data.skipped > 0) msg.push(`${data.skipped} already monitored`);
        if (data.errors?.length > 0) msg.push(`${data.errors.length} failed`);
        toast.success(msg.join('. '));
        setSelectedIds(new Set());
        setShowPromoteModal(false);
      } else {
        toast.error(data.error || 'Failed to promote devices');
      }
    } catch (error) {
      console.error('Promote error:', error);
      toast.error('Failed to promote devices');
    } finally {
      setPromoting(false);
    }
  };


  // Delete selected discovered devices
  const handleDeleteSelected = async () => {
    if (selectedIds.size === 0) return;
    if (!confirm('Remove ' + selectedIds.size + ' device(s) from discovery? Monitored devices cannot be removed.')) return;
    setDeleting(true);
    try {
      const response = await fetchWithTenant('/api/md/devices', {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ deviceIds: Array.from(selectedIds) }),
      });
      const data = await response.json();
      if (response.ok && data.success) {
        toast.success(data.deleted + ' device(s) removed');
        setSelectedIds(new Set());
        fetchDevices();
      } else {
        toast.error(data.error || 'Failed to delete devices');
      }
    } catch (error) {
      toast.error('Failed to delete devices');
    } finally {
      setDeleting(false);
    }
  };

  // Rescan selected devices
  const handleRescanSelected = async () => {
    if (selectedIds.size === 0) return;
    setRescanning(true);
    try {
      const response = await fetchWithTenant('/api/md/devices/rescan', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ deviceIds: Array.from(selectedIds) }),
      });
      const data = await response.json();
      if (response.ok && data.success) {
        toast.success('Rescanning ' + data.ips.length + ' device(s)...');
        setSelectedIds(new Set());
        setTimeout(() => fetchDevices(true), 5000);
      } else {
        toast.error(data.error || 'Failed to rescan');
      }
    } catch (error) {
      toast.error('Failed to rescan devices');
    } finally {
      setRescanning(false);
    }
  };

  // Update device type
  const handleSaveDeviceType = async () => {
    if (!editTypeDevice || !editTypeValue) return;
    try {
      const response = await fetchWithTenant('/api/md/devices/' + editTypeDevice.id + '/type', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ deviceType: editTypeValue }),
      });
      const data = await response.json();
      if (response.ok && data.success) {
        toast.success('Device type updated');
        setEditTypeDevice(null);
        fetchDevices();
      } else {
        toast.error(data.error || 'Failed to update type');
      }
    } catch (error) {
      toast.error('Failed to update device type');
    }
  };
  // Initial load
  useEffect(() => {
    fetchDevices();

    fetchAgents();
    fetchSyncStatus();

    const interval = setInterval(fetchDevices, 30000);
    return () => clearInterval(interval);
  }, [fetchDevices, fetchAgents, fetchSyncStatus]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (showSyncDropdown && !e.target.closest('.md-sync-dropdown-container')) {
        setShowSyncDropdown(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [showSyncDropdown]);

  // Filter devices
  const filteredDevices = devices.filter(device => {
    if (activeFilter === 'cameras' && device.deviceType !== 'CAMERA') return false;
    if (activeFilter === 'accessControl' && device.deviceType !== 'ACCESS_CONTROLLER') return false;
    if (activeFilter === 'network' && !['ROUTER', 'SWITCH', 'ACCESS_POINT'].includes(device.deviceType)) return false;
    if (activeFilter === 'servers' && device.deviceType !== 'SERVER') return false;
    if (activeFilter === 'online' && device.status !== 'ONLINE') return false;
    if (activeFilter === 'wireless' && !device.isWireless) return false;

    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      const matchesIp = device.ipAddress?.toLowerCase().includes(search);
      const matchesHostname = device.hostname?.toLowerCase().includes(search);
      const matchesManufacturer = device.manufacturer?.toLowerCase().includes(search);
      const matchesMac = device.macAddress?.toLowerCase().includes(search);
      if (!matchesIp && !matchesHostname && !matchesManufacturer && !matchesMac) return false;
    }

    if (statusFilter && device.status !== statusFilter) return false;
    return true;
  }).sort((a, b) => compareIPs(a.ipAddress, b.ipAddress));

  // Agent picker options -- label shows name + IP (falling back to hostname,
  // then just the name), suffixed with online/offline status.
  const agentOptions = eligibleAgents.map(a => {
    const online = a.isOnline || a.status === 'ONLINE';
    const addr = a.localIp || a.hostname;
    const label = addr ? `${a.name} (${addr})` : a.name;
    return (
      <option key={a.id} value={a.id}>{label} — {online ? 'Online' : 'Offline'}</option>
    );
  });

  return (
    <div className="page-content">
      {/* Header */}
      <div className="page-header-with-actions">
        <div>
          <h1 className="page-title">
            <Monitor size={28} />
            Devices
          </h1>
          <p className="page-subtitle">Scan and discover devices on your network</p>
        </div>
        <div className="page-header-actions">
          <button className="tutorial-help-btn" onClick={() => startTutorial(discoveryTutorial.module, discoveryTutorial.steps)} title="Take a tour" aria-label="Take a tour">
            <HelpCircle size={16} />
          </button>
          {selectedIds.size > 0 && (
            <button
              className="btn btn-accent"
              onClick={() => setShowPromoteModal(true)}
              title="Add selected devices to monitoring"
            >
              <Shield size={16} />
              Monitor Selected ({selectedIds.size})
            </button>
          )}
          {scanning ? (
            <button
              className="btn btn-danger"
              onClick={cancelScan}
              title="Cancel the current scan"
            >
              <XCircle size={16} />
              Cancel Scan
            </button>
          ) : (
            <button
              className="btn btn-primary"
              onClick={triggerScan}
              disabled={!selectedAgent}
              title={!selectedAgent ? 'Select an agent to scan with' : (isSelectedAgentOnline ? 'Scan the network for devices' : `${selectedAgent.name} is offline`)}
            >
              <Play size={16} />
              Scan Network
            </button>
          )}
          <button
            className="btn btn-secondary"
            onClick={() => setShowScanConfig(true)}
            title="Configure scan networks and schedule"
          >
            <Settings size={16} />
          </button>
        </div>
      </div>

      {/* Agent Status */}
      <MdLicenseBanner />
        <DevicesTabBar />
      {agents.length > 0 && (
        <div className="md-agent-status">
          <span className={`status-indicator ${isSelectedAgentOnline ? 'online' : 'offline'}`}>
            {isSelectedAgentOnline ? 'Agent Online' : 'Agent Offline'}
          </span>
          {eligibleAgents.length > 0 ? (
            <select
              className="form-select"
              value={selectedAgentId}
              onChange={(e) => setSelectedAgentId(e.target.value)}
              title="Select which agent runs the scan"
            >
              {agentOptions}
            </select>
          ) : (
            <span className="md-agent-name">No scan-capable agent (all paired agents are host-collector only)</span>
          )}
        </div>
      )}


      {/* Scan Progress Bar */}
      {scanProgress && scanProgress.status !== 'idle' && (
        <div className="md-scan-progress">
          <div className="md-scan-progress-header">
            <Loader2 size={16} className="spin" />
            <span className="md-scan-progress-label">
              {scanProgress.status === 'complete' 
                ? 'Scan complete \u2014 ' + (scanProgress.devicesFound || 0) + ' devices found'
                : 'Phase ' + scanProgress.phase + '/' + scanProgress.totalPhases + ': ' + scanProgress.phaseName}
            </span>
          </div>
          <div className="md-scan-progress-bar">
            <div 
              className={'md-scan-progress-fill' + (scanProgress.status === 'complete' ? ' complete' : '')}
              style={{ width: Math.max(5, (scanProgress.phase / scanProgress.totalPhases) * 100) + '%' }}
            />
          </div>
          <div className="md-scan-phases">
            {['Ping Sweep', 'Multicast', 'Port Scan', 'Merge', 'SNMP', 'Topology', 'Cameras', 'BMC'].map((name, i) => (
              <span 
                key={i} 
                className={'md-scan-phase' + (i + 1 < scanProgress.phase ? ' done' : i + 1 === scanProgress.phase ? ' active' : '')}
              >
                {name}
              </span>
            ))}
          </div>
        </div>
      )}
      {/* Stats Cards - Cameras, Access Control, Servers, Network, Online */}
      <div className="md-stats-grid">
        <div
          className={`md-stat-card ${activeFilter === 'all' ? 'active' : ''}`}
          onClick={() => setActiveFilter('all')}
        >
          <div className="md-stat-icon all"><Monitor size={24} /></div>
          <div className="md-stat-info">
            <span className="md-stat-value">{stats.total}</span>
            <span className="md-stat-label">All Devices</span>
          </div>
        </div>
        <div
          className={`md-stat-card ${activeFilter === 'cameras' ? 'active' : ''}`}
          onClick={() => setActiveFilter(activeFilter === 'cameras' ? 'all' : 'cameras')}
        >
          <div className="md-stat-icon camera"><Camera size={24} /></div>
          <div className="md-stat-info">
            <span className="md-stat-value">{stats.cameras}</span>
            <span className="md-stat-label">Cameras</span>
          </div>
        </div>
        <div
          className={`md-stat-card ${activeFilter === 'accessControl' ? 'active' : ''}`}
          onClick={() => setActiveFilter(activeFilter === 'accessControl' ? 'all' : 'accessControl')}
        >
          <div className="md-stat-icon access-control"><DoorOpen size={24} /></div>
          <div className="md-stat-info">
            <span className="md-stat-value">{stats.accessControl}</span>
            <span className="md-stat-label">Access Control</span>
          </div>
        </div>
        <div
          className={`md-stat-card ${activeFilter === 'servers' ? 'active' : ''}`}
          onClick={() => setActiveFilter(activeFilter === 'servers' ? 'all' : 'servers')}
        >
          <div className="md-stat-icon server"><Server size={24} /></div>
          <div className="md-stat-info">
            <span className="md-stat-value">{stats.servers}</span>
            <span className="md-stat-label">Servers</span>
          </div>
        </div>
        <div
          className={`md-stat-card ${activeFilter === 'network' ? 'active' : ''}`}
          onClick={() => setActiveFilter(activeFilter === 'network' ? 'all' : 'network')}
        >
          <div className="md-stat-icon network"><Network size={24} /></div>
          <div className="md-stat-info">
            <span className="md-stat-value">{stats.network}</span>
            <span className="md-stat-label">Network</span>
          </div>
        </div>
        <div
          className={`md-stat-card ${activeFilter === 'wireless' ? 'active' : ''}`}
          onClick={() => setActiveFilter(activeFilter === 'wireless' ? 'all' : 'wireless')}
        >
          <div className="md-stat-icon network"><Wifi size={24} /></div>
          <div className="md-stat-info">
            <span className="md-stat-value">{stats.wireless}</span>
            <span className="md-stat-label">Wireless</span>
          </div>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="md-filters">
        <div className="md-search">
          <Search size={16} />
          <input
            type="text"
            className="form-input"
            placeholder="Search by IP, hostname, MAC, or manufacturer..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <select
          className="form-select"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          <option value="">All Status</option>
          <option value="ONLINE">Online</option>
          <option value="OFFLINE">Offline</option>
          <option value="UNKNOWN">Unknown</option>
          <option value="STALE">Stale</option>
        </select>
        
        {/* Sync Integrations Dropdown */}
        <div className="md-sync-dropdown-container">
          <button
            className="btn btn-secondary btn-sm md-sync-btn"
            onClick={(e) => {
              e.stopPropagation();
              setShowSyncDropdown(!showSyncDropdown);
            }}
            disabled={syncing}
          >
            {syncing ? (
              <>
                <Loader2 size={14} className="spin" />
                Syncing...
              </>
            ) : (
              <>
                <Link2 size={14} />
                Sync Integrations
                <ChevronDown size={14} />
              </>
            )}
          </button>
          
          {showSyncDropdown && (
            <div className="md-sync-dropdown">
              <div className="md-sync-dropdown-header">
                <span>Import from Integrations</span>
              </div>
              
              {syncStatus.length > 0 ? (
                <div className="md-sync-status-list">
                  {syncStatus.map(integration => (
                    <div key={integration.integrationId} className="md-sync-status-item">
                      <div className="md-sync-status-info">
                        <span className="md-sync-status-name">{integration.integrationName}</span>
                        <span className="md-sync-status-type">
                          {integration.integrationType === 'WAVE_VMS' && 'Wave VMS'}
                          {integration.integrationType === 'MIRASYS_VMS' && 'Mirasys VMS'}
                          {integration.integrationType === 'FEENICS_ACCESS' && 'Feenics Access'}
                        </span>
                      </div>
                      <span className="md-sync-status-count">
                        {integration.devices?.total || 0} devices
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="md-sync-empty">
                  No integrations configured
                </div>
              )}
              
              <div className="md-sync-dropdown-footer">
                <button 
                  className="btn btn-primary btn-sm"
                  onClick={syncIntegrations}
                  disabled={syncing || syncStatus.length === 0}
                >
                  <Link2 size={14} />
                  Sync All Now
                </button>
              </div>
            </div>
          )}
        </div>
        
        <button
          className="btn btn-secondary btn-sm"
          onClick={() => fetchDevices(true)}
          disabled={loading}
          title="Refresh device list from database"
        >
          <RefreshCw size={14} className={loading ? 'spin' : ''} />
          Refresh
        </button>
        
        {/* Total Devices Pill */}
        <div className="md-total-pill">
          <Monitor size={14} />
          <span>{stats.total}</span>
          <span className="md-total-pill-label">devices</span>
        </div>
      </div>

      {/* Selection banner */}
      {selectedIds.size > 0 && (
        <div className="md-selection-banner">
          <span>{selectedIds.size} device{selectedIds.size > 1 ? 's' : ''} selected</span>
          <div className="md-selection-actions">
            <button className="btn btn-accent btn-sm" onClick={() => setShowPromoteModal(true)}>
              <Shield size={14} /> Monitor Selected
            </button>
            <button className="btn btn-secondary btn-sm" onClick={handleRescanSelected} disabled={rescanning}>
              {rescanning ? <Loader2 size={14} className="spin" /> : <RotateCcw size={14} />} Rescan Selected
            </button>
            <button className="btn btn-danger btn-sm" onClick={handleDeleteSelected} disabled={deleting}>
              {deleting ? <Loader2 size={14} className="spin" /> : <Trash2 size={14} />} Delete Selected
            </button>
            <button className="btn btn-secondary btn-sm" onClick={() => setSelectedIds(new Set())}>
              Clear Selection
            </button>
          </div>
        </div>
      )}

      {/* Devices Table */}
      <div className="card">
        {loading ? (
          <div className="md-loading">
            <Loader2 size={32} className="spin" />
            <p>Loading devices...</p>
          </div>
        ) : filteredDevices.length === 0 ? (
          <div className="md-empty">
            <Monitor size={48} />
            <h3>No devices found</h3>
            <p>
              {devices.length === 0
                ? 'Run a network scan to discover devices'
                : 'No devices match your filters'}
            </p>
            {devices.length === 0 && selectedAgent && (
              <button className="btn btn-primary" onClick={triggerScan}>
                <Play size={16} /> Start Scan
              </button>
            )}
          </div>
        ) : (
          <>
          <div className="md-table-container md-discovery-table-container">
            <table className="md-devices-table">
              <thead>
                <tr>
                  <th className="md-checkbox-col">
                    <input
                      type="checkbox"
                      checked={selectedIds.size === filteredDevices.length && filteredDevices.length > 0}
                      onChange={toggleSelectAll}
                      title="Select all"
                    />
                  </th>
                  <th>IP Address</th>
                  <th>Type</th>
                  <th>Manufacturer</th>
                  <th>Model</th>
                  <th>Hostname</th>
                  <th>Status</th>
                  <th>Monitored</th>
                  <th>Last Seen</th>
                </tr>
              </thead>
              <tbody>
                {filteredDevices.map(device => {
                  const Icon = deviceIcons[device.deviceType] || Monitor;
                  const isSelected = selectedIds.has(device.id);
                  return (
                    <tr
                      key={device.id}
                      className={`clickable-row ${isSelected ? 'md-row-selected' : ''}`}
                      onClick={(e) => {
                        if (e.target.type === 'checkbox' || e.target.closest('.md-checkbox-col') || e.target.closest('.device-badge.editable')) return;
                        navigate('/monitoring/discovery/' + device.id);
                      }}
                    >
                      <td className="md-checkbox-col">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => toggleSelect(device.id)}
                        />
                      </td>
                      <td>
                        <div className="md-ip-cell">
                          <code>{device.ipAddress}</code>
                          {device.macAddress && (
                            <span className="md-mac">{device.macAddress}</span>
                          )}
                        </div>
                      </td>
                      <td>
                        <span className={`device-badge ${device.deviceType?.toLowerCase()} editable`}
                          onClick={(e) => { e.stopPropagation(); setEditTypeDevice(device); setEditTypeValue(device.deviceType); }}
                          title="Click to change device type">
                          <Icon size={12} />
                          {device.deviceType}
                          <Edit3 size={10} className="edit-type-icon" />
                        </span>
                        {(device.onvifCapable || device.onvifProfiles?.length > 0) && (
                          <span className="md-protocol-badge onvif" title={device.onvifProfiles?.length > 0 ? `Profiles: ${device.onvifProfiles.join(', ')}` : (device.onvifXAddr ? 'ONVIF Confirmed' : 'ONVIF Capable')}>
                            ONVIF
                          </span>
                        )}
                        {device.rtspCapable && (
                          <span className="md-protocol-badge rtsp" title={Array.isArray(device.rtspStreams) && device.rtspStreams.length > 0 ? `${device.rtspStreams.length} stream(s) detected` : 'RTSP Capable'}>RTSP</span>
                        )}
                        {device.isWireless && (
                          <span className="md-protocol-badge wireless" title={`${device.wirelessSsid || 'Unknown SSID'}${device.wirelessSignalDbm != null ? ` · ${device.wirelessSignalDbm} dBm` : ''}`}>
                            <Wifi size={10} /> Wi-Fi
                          </span>
                        )}
                      </td>
                      <td>{device.manufacturer || '—'}</td>
                      <td className="md-model-cell">{device.model || '—'}</td>
                      <td>
                        <span className="md-hostname-cell" title={device.hostname}>
                          {device.hostname || '—'}
                        </span>
                      </td>
                      <td>
                        <StatusTooltip status={device.status}>
                          <span className={`status-indicator ${device.status?.toLowerCase()}`}>
                            {device.status === 'STALE' ? `Not seen since ${formatRelativeTime(device.lastSeenAt)}` : device.status}
                          </span>
                        </StatusTooltip>
                      </td>
                      <td>
                        {device.isMonitored ? (
                          <span className="md-monitored-badge">Monitoring</span>
                        ) : (
                          <span className="md-not-monitored">—</span>
                        )}
                      </td>
                      <td>
                        <span className="md-time-cell">
                          <Clock size={12} />
                          {formatRelativeTime(device.lastSeenAt)}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Mobile card view: shown <=768px (table is hidden there). Renders
              from the same filteredDevices array and preserves selection, type
              editing and navigation. */}
          <div className="mdd-mobile-cards">
            {filteredDevices.map(device => {
              const Icon = deviceIcons[device.deviceType] || Monitor;
              const isSelected = selectedIds.has(device.id);
              const isExpanded = expandedCards.has(device.id);
              const hasOnvif = device.onvifCapable || device.onvifProfiles?.length > 0;
              return (
                <div
                  key={device.id}
                  className={`mdd-card ${isExpanded ? 'expanded' : ''} ${isSelected ? 'selected' : ''}`}
                  onClick={() => toggleCard(device.id)}
                >
                  <div className="mdd-card-main">
                    <div className="mdd-card-row1">
                      <label className="mdd-card-check" onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => toggleSelect(device.id)}
                        />
                      </label>
                      <code className="mdd-card-ip">{device.ipAddress}</code>
                      <span className={`device-badge ${device.deviceType?.toLowerCase()}`}>
                        <Icon size={12} />
                        {device.deviceType}
                      </span>
                      <ChevronDown size={16} className={`mdd-card-chevron ${isExpanded ? 'open' : ''}`} />
                    </div>
                    <div className="mdd-card-row2">
                      <StatusTooltip status={device.status}>
                        <span className={`status-indicator ${device.status?.toLowerCase()}`}>
                          {device.status === 'STALE' ? `Not seen since ${formatRelativeTime(device.lastSeenAt)}` : device.status}
                        </span>
                      </StatusTooltip>
                      {device.isMonitored
                        ? <span className="md-monitored-badge">Monitoring</span>
                        : <span className="md-not-monitored">Not monitored</span>}
                    </div>
                  </div>

                  <div className={`mdd-card-expand ${isExpanded ? 'open' : ''}`}>
                    <div className="mdd-card-details">
                      <div className="mdd-card-detail">
                        <span className="mdd-card-detail-label">Manufacturer</span>
                        <span className="mdd-card-detail-value">{device.manufacturer || '---'}</span>
                      </div>
                      <div className="mdd-card-detail">
                        <span className="mdd-card-detail-label">Model</span>
                        <span className="mdd-card-detail-value">{device.model || '---'}</span>
                      </div>
                      <div className="mdd-card-detail">
                        <span className="mdd-card-detail-label">Hostname</span>
                        <span className="mdd-card-detail-value">{device.hostname || '---'}</span>
                      </div>
                      <div className="mdd-card-detail">
                        <span className="mdd-card-detail-label">MAC</span>
                        <span className="mdd-card-detail-value">{device.macAddress || '---'}</span>
                      </div>
                      <div className="mdd-card-detail">
                        <span className="mdd-card-detail-label">Last seen</span>
                        <span className="mdd-card-detail-value">{formatRelativeTime(device.lastSeenAt)}</span>
                      </div>
                      <div className="mdd-card-detail">
                        <span className="mdd-card-detail-label">Type</span>
                        <span className="mdd-card-detail-value">
                          <span className={`device-badge ${device.deviceType?.toLowerCase()} editable`}
                            onClick={(e) => { e.stopPropagation(); setEditTypeDevice(device); setEditTypeValue(device.deviceType); }}
                            title="Click to change device type">
                            <Icon size={12} />
                            {device.deviceType}
                            <Edit3 size={10} className="edit-type-icon" />
                          </span>
                        </span>
                      </div>
                      {(hasOnvif || device.rtspCapable || device.isWireless) && (
                        <div className="mdd-card-detail mdd-card-detail-full">
                          <span className="mdd-card-detail-label">Protocols</span>
                          <span className="mdd-card-detail-value mdd-card-protocols">
                            {hasOnvif && (
                              <span className="md-protocol-badge onvif">ONVIF</span>
                            )}
                            {device.rtspCapable && (
                              <span className="md-protocol-badge rtsp">RTSP</span>
                            )}
                            {device.isWireless && (
                              <span className="md-protocol-badge wireless" title={`${device.wirelessSsid || 'Unknown SSID'}${device.wirelessSignalDbm != null ? ` · ${device.wirelessSignalDbm} dBm` : ''}`}>
                                <Wifi size={10} /> Wi-Fi
                              </span>
                            )}
                          </span>
                        </div>
                      )}
                      <div className="mdd-card-actions">
                        <button
                          className="mdd-card-action-btn"
                          onClick={(e) => { e.stopPropagation(); navigate('/monitoring/discovery/' + device.id); }}
                        >
                          <Eye size={14} /> View details
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          </>
        )}

        {!loading && filteredDevices.length > 0 && (
          <div className="md-table-footer">
            Showing {filteredDevices.length} of {devices.length} devices
            {activeFilter !== 'all' && (
              <button className="btn-link" onClick={() => setActiveFilter('all')}>
                Clear filter
              </button>
            )}
          </div>
        )}
      </div>

      {/* Promote Modal */}
      {showPromoteModal && (
        <div className="modal-overlay" onClick={() => setShowPromoteModal(false)}>
          <div className="modal-content md-promote-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2><Shield size={20} /> Add to Monitoring</h2>
              <button className="modal-close" onClick={() => setShowPromoteModal(false)}>
                <X size={20} />
              </button>
            </div>

            <div className="modal-body">
              <p className="md-promote-summary">
                Adding <strong>{selectedIds.size}</strong> device{selectedIds.size > 1 ? 's' : ''} to active monitoring.
                Configure default settings below.
              </p>

              <div className="md-promote-form">
                <div className="form-group">
                  <label>Monitoring Profile</label>
                  <select
                    className="form-select"
                    value={promoteConfig.monitoringProfile}
                    onChange={(e) => setPromoteConfig(p => ({ ...p, monitoringProfile: e.target.value }))}
                  >
                    <option value="PING">Ping (ICMP)</option>
                    <option value="HTTP_CHECK">HTTP Health Check</option>
                    <option value="RTSP_CHECK">RTSP Stream Check</option>
                    <option value="API_HEALTH">API Health Check</option>
                    <option value="SNMP_POLL">SNMP Poll</option>
                    <option value="WMI">WMI (Windows)</option>
                  </select>
                  <span className="form-hint">How the device will be checked</span>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Poll Interval</label>
                    <select
                      className="form-select"
                      value={promoteConfig.pollIntervalSeconds}
                      onChange={(e) => setPromoteConfig(p => ({ ...p, pollIntervalSeconds: parseInt(e.target.value) }))}
                    >
                      <option value={30}>Every 30 seconds</option>
                      <option value={60}>Every 1 minute</option>
                      <option value={120}>Every 2 minutes</option>
                      <option value={300}>Every 5 minutes</option>
                      <option value={600}>Every 10 minutes</option>
                    </select>
                    <span className="form-hint">How often to check</span>
                  </div>

                  <div className="form-group">
                    <label>Alert After</label>
                    <select
                      className="form-select"
                      value={promoteConfig.alertThresholdSeconds}
                      onChange={(e) => setPromoteConfig(p => ({ ...p, alertThresholdSeconds: parseInt(e.target.value) }))}
                    >
                      <option value={60}>1 minute offline</option>
                      <option value={120}>2 minutes offline</option>
                      <option value={300}>5 minutes offline</option>
                      <option value={600}>10 minutes offline</option>
                      <option value={900}>15 minutes offline</option>
                    </select>
                    <span className="form-hint">When to trigger alert</span>
                  </div>
                </div>

                <div className="form-group">
                  <label>Group (optional)</label>
                  <input
                    type="text"
                    className="form-input"
                    placeholder="e.g. Building A, Lobby Cameras, Server Room..."
                    value={promoteConfig.group}
                    onChange={(e) => setPromoteConfig(p => ({ ...p, group: e.target.value }))}
                  />
                  <span className="form-hint">Organize devices into groups</span>
                </div>
              </div>

              {/* Selected devices preview */}
              <div className="md-promote-preview">
                <h4>Selected Devices</h4>
                <div className="md-promote-device-list">
                  {devices
                    .filter(d => selectedIds.has(d.id))
                    .map(d => (
                      <div key={d.id} className="md-promote-device-item">
                        <code>{d.ipAddress}</code>
                        <span className={`device-badge sm ${d.deviceType?.toLowerCase()}`}>
                          {d.deviceType}
                        </span>
                        <span className="md-promote-device-name">
                          {d.hostname || d.manufacturer || ''}
                        </span>
                      </div>
                    ))}
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowPromoteModal(false)}>
                Cancel
              </button>
              <button
                className="btn btn-accent"
                onClick={handlePromote}
                disabled={promoting}
              >
                {promoting ? (
                  <>
                    <Loader2 size={16} className="spin" />
                    Adding...
                  </>
                ) : (
                  <>
                    <Shield size={16} />
                    Start Monitoring ({selectedIds.size})
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Scan Config Modal */}
      {showScanConfig && (
        <div className="modal-overlay" onClick={() => setShowScanConfig(false)}>
          <div className="modal-content md-promote-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2><Settings size={20} /> Discovery Settings</h2>
              <button className="modal-close" onClick={() => setShowScanConfig(false)}>
                <X size={20} />
              </button>
            </div>
            <div className="modal-body">
              <div className="md-promote-form">
                <div className="form-group">
                  <label>Agent</label>
                  <p className="form-hint" style={{marginTop: 0, marginBottom: "0.5rem"}}>Which agent this scan configuration applies to.</p>
                  <select
                    className="form-select"
                    value={selectedAgentId}
                    onChange={(e) => setSelectedAgentId(e.target.value)}
                  >
                    {agentOptions}
                  </select>
                </div>
                <div className="form-group">
                  <label>Scan Networks</label>
                  <p className="form-hint" style={{marginTop: 0, marginBottom: "0.5rem"}}>Add subnets to scan. Use CIDR notation (e.g. 192.168.1.0/24) or single IPs.</p>
                  <div style={{display: "flex", gap: "0.5rem", marginBottom: "0.5rem"}}>
                    <input
                      type="text"
                      className="form-input"
                      placeholder="192.168.1.0/24"
                      value={networkInput}
                      onChange={(e) => setNetworkInput(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addNetwork())}
                    />
                    <button className="btn btn-primary btn-sm" onClick={addNetwork} type="button">Add</button>
                  </div>
                  {scanConfig.defaultNetworks.length > 0 && (
                    <div style={{display: "flex", flexWrap: "wrap", gap: "0.5rem"}}>
                      {scanConfig.defaultNetworks.map((net, i) => (
                        <span key={i} style={{display: "inline-flex", alignItems: "center", gap: "0.25rem", background: "var(--bg-tertiary, #2a2a3e)", padding: "0.25rem 0.75rem", borderRadius: "1rem", fontSize: "0.85rem"}}>
                          <Network size={14} /> {net}
                          <button onClick={() => removeNetwork(i)} style={{background: "none", border: "none", cursor: "pointer", color: "var(--text-secondary)", padding: "0 0.25rem", fontSize: "1rem"}}>&times;</button>
                        </span>
                      ))}
                    </div>
                  )}
                  {scanConfig.defaultNetworks.length === 0 && (
                    <p className="form-hint" style={{color: "var(--warning, #f59e0b)"}}>No networks configured. Agent will auto-detect from local interfaces.</p>
                  )}
                </div>
                <hr style={{border: "none", borderTop: "1px solid var(--border-color, #333)", margin: "1rem 0"}} />
                <div className="form-group">
                  <label style={{display: "flex", alignItems: "center", gap: "0.5rem"}}>
                    <input
                      type="checkbox"
                      checked={scanConfig.schedule.enabled}
                      onChange={(e) => setScanConfig(c => ({ ...c, schedule: { ...c.schedule, enabled: e.target.checked }}))}
                    />
                    Scheduled Discovery
                  </label>
                  <span className="form-hint">Automatically run discovery at a set time daily</span>
                </div>
                {scanConfig.schedule.enabled && (
                  <div className="form-group">
                    <label>Scan Time</label>
                    <input
                      type="time"
                      className="form-input"
                      value={scanConfig.schedule.time}
                      onChange={(e) => setScanConfig(c => ({ ...c, schedule: { ...c.schedule, time: e.target.value }}))}
                      style={{maxWidth: "10rem"}}
                    />
                    <span className="form-hint">Server local time (24-hour format)</span>
                  </div>
                )}
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowScanConfig(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={saveScanConfig} disabled={savingScanConfig || !selectedAgent}>
                {savingScanConfig ? <><Loader2 size={16} className="spin" /> Saving...</> : "Save Configuration"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Device Type Modal */}
      {editTypeDevice && (
        <div className="modal-overlay" onClick={() => setEditTypeDevice(null)}>
          <div className="modal-content md-type-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2><Edit3 size={20} /> Change Device Type</h2>
              <button className="modal-close" onClick={() => setEditTypeDevice(null)}><X size={20} /></button>
            </div>
            <div className="modal-body">
              <p className="md-type-device-info">{editTypeDevice.ipAddress} {editTypeDevice.manufacturer ? '(' + editTypeDevice.manufacturer + ')' : ''}</p>
              <div className="form-group">
                <label>Device Type</label>
                <select className="form-select" value={editTypeValue} onChange={(e) => setEditTypeValue(e.target.value)}>
                  <option value="UNKNOWN">Unknown</option>
                  <option value="CAMERA">Camera</option>
                  <option value="NVR">NVR</option>
                  <option value="DVR">DVR</option>
                  <option value="VMS_SERVER">VMS Server</option>
                  <option value="ACCESS_CONTROLLER">Access Controller</option>
                  <option value="INTERCOM">Intercom</option>
                  <option value="SWITCH">Switch</option>
                  <option value="ROUTER">Router</option>
                  <option value="ACCESS_POINT">Access Point</option>
                  <option value="FIREWALL">Firewall</option>
                  <option value="SERVER">Server</option>
                  <option value="WORKSTATION">Workstation</option>
                  <option value="IOT_DEVICE">IoT Device</option>
                  <option value="PRINTER">Printer</option>
                  <option value="PHONE">Phone</option>
                  <option value="NAS">NAS</option>
                  <option value="UPS">UPS</option>
                  <option value="PDU">PDU</option>
                  <option value="OTHER">Other</option>
                </select>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setEditTypeDevice(null)}>Cancel</button>
              <button className="btn btn-accent" onClick={handleSaveDeviceType}>Save</button>
            </div>
          </div>
        </div>
      )}

      {showPrompt && (
        <TutorialPrompt
          module={discoveryTutorial.module}
          moduleName={discoveryTutorial.moduleName}
          steps={discoveryTutorial.steps}
          onClose={dismissTutorial}
        />
      )}
    </div>
  );
};

export default MdDiscovery;
